import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import type { AxiosError } from "axios";
import KalixLogo from "../../components/svgElements/KalixLogo";
import InputWrapper from "../../components/formElements/InputWrapper";
import Textbox from "../../components/formElements/Textbox";
import { Link } from "react-router-dom";
import Button from "../../components/formElements/Button";
import { ROUTES } from "../../constants/routes";
import { login } from "../../services/auth";
import {
  EMAIL_REGEX,
  EMAIL_MIN_LENGTH,
  EMAIL_MAX_LENGTH,
} from "../../utils/constant";
import {
  setAuthToken,
  setRefreshToken,
  setUserInfo,
  showErrorToast,
  showSuccessToast,
} from "../../utils/helper";

import { useAppDispatch } from "../../redux/hooks";
import { setUser } from "../../redux/slices/authSlice";
import type { ILoginCredentials } from "../../interfaces";
import "./Auth.scss";

const currentYear = new Date().getFullYear();

const Login = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<ILoginCredentials>({
    mode: "onSubmit",
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const onSubmit = async (data: ILoginCredentials) => {
    try {
      setIsLoading(true);
      const { email, password } = data;
      const response = await login({ email, password, type: "admin" });

      if (response.success && response.data) {
        // Store tokens from response body (current backend).
        // When backend switches to Set-Cookie these will be absent and cookies take over.
        if (response.data.accessToken) setAuthToken(response.data.accessToken);
        if (response.data.refreshToken) setRefreshToken(response.data.refreshToken);
        if (response.data.user) {
          setUserInfo(response.data.user);
          dispatch(setUser(response.data.user));
        }

        showSuccessToast(response.message || t("auth.loginSuccess"));
        navigate(ROUTES.DASHBOARD);
      } else {
        showErrorToast(response.message || t("auth.loginFailed"));
      }
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      const errorMessage =
        axiosError?.response?.data?.message ||
        axiosError?.message ||
        t("auth.loginError");
      showErrorToast(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <div className="userauth-main">
      <div className="row g-3">
        <div className="col-md-7">
          <div className="userauth-bg" />
        </div>

        <div className="col-md-5">
          <div className="userauth-form">
            <div className="userauth-logo">
              <KalixLogo />
            </div>
            <div className="userauth-form-content">
              <h2 className="text-center">{t("auth.signIn")}</h2>
              <p className="text-center">{t("auth.signInDescription")}</p>
              <form onSubmit={handleSubmit(onSubmit)}>
                <InputWrapper>
                  <InputWrapper.Label required>
                    {t("auth.email")}
                  </InputWrapper.Label>
                  <Textbox
                    type="email"
                    name="email"
                    placeholder={t("auth.enterEmail")}
                    control={control}
                    className="form-control"
                    rules={{
                      required: t("validation.emailRequired"),
                      minLength: {
                        value: EMAIL_MIN_LENGTH,
                        message: t("validation.emailMinLength", {
                          count: EMAIL_MIN_LENGTH,
                        }),
                      },
                      maxLength: {
                        value: EMAIL_MAX_LENGTH,
                        message: t("validation.emailMaxLength", {
                          count: EMAIL_MAX_LENGTH,
                        }),
                      },
                      pattern: {
                        value: EMAIL_REGEX,
                        message: t("validation.emailInvalid"),
                      },
                    }}
                  />
                  {errors.email && (
                    <span className="auth-msg error">
                      {errors.email.message}
                    </span>
                  )}
                </InputWrapper>

                <InputWrapper>
                  <InputWrapper.Label required>
                    {t("auth.password")}
                  </InputWrapper.Label>
                  <Textbox
                    type="password"
                    name="password"
                    placeholder={t("auth.enterPassword")}
                    control={control}
                    className="form-control"
                    icon={true}
                    showPassword={true}
                    rules={{
                      required: t("validation.passwordRequired"),
                    }}
                  />
                  {errors.password && (
                    <span className="auth-msg error">
                      {errors.password.message}
                    </span>
                  )}
                </InputWrapper>

                <div className="button-align justify-content-end remember-box">
                  <Link to={ROUTES.FORGOT_PASSWORD} className="color-primary">
                    {t("auth.forgotPasswordLink")}
                  </Link>
                </div>

                <Button
                  type="submit"
                  className="primary-btn rounded-md w-100"
                  disabled={isLoading}
                >
                  {isLoading ? t("common.loading") : t("auth.signIn")}
                </Button>
              </form>
            </div>
            <p className="copyright-text">
              {t("auth.copyright", { year: currentYear })}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
